import React, { useState, useEffect } from 'react';
import { 
    DollarSign, TrendingUp, Wallet, Clock, 
    ArrowUpRight, ArrowDownLeft, CheckCircle, 
    AlertCircle, RefreshCw, Send, History, 
    CreditCard, Building, Info, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Earnings = () => {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [withdrawModal, setWithdrawModal] = useState(false);
    const [withdrawData, setWithdrawData] = useState({
        amount: '',
        paymentMethod: 'mpesa',
        paymentDetails: user?.phoneNumber || '',
        lecturerNote: ''
    });

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

    useEffect(() => {
        fetchSummary();
    }, []);

    const fetchSummary = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/earnings/my-summary`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSummary(response.data);
            setWithdrawData(prev => ({ ...prev, amount: response.data.totalPending }));
        } catch (error) {
            console.error('Error fetching earnings:', error);
            setMessage({ type: 'error', text: 'Failed to load earnings summary.' });
        } finally {
            setLoading(false);
        }
    };

    const handleWithdrawRequest = async (e) => {
        e.preventDefault();
        if (!withdrawData.amount || parseFloat(withdrawData.amount) <= 0) {
            setMessage({ type: 'error', text: 'Please enter a valid amount.' });
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/earnings/withdraw`, withdrawData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setMessage({ type: 'success', text: 'Withdrawal request submitted! It will be reviewed by the admin.' });
            setWithdrawModal(false);
            fetchSummary();
        } catch (error) {
            console.error('Withdrawal error:', error);
            setMessage({ type: 'error', text: error.response?.data?.message || 'Withdrawal request failed.' });
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <RefreshCw className="h-10 w-10 text-indigo-600 animate-spin" />
            <p className="text-gray-500 font-medium">Calculating your earnings...</p>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto space-y-8 animate-fadeIn p-4 md:p-8">
            {/* Header section with Balance Card */}
            <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1 w-full space-y-6">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Earning Dashboard</h1>
                        <p className="text-gray-500 mt-1">Track your commissions and manage payouts.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {summary?.compensationMethod === 'salary' ? (
                            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl p-8 text-white shadow-xl shadow-indigo-200 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform">
                                    <Building className="h-32 w-32" />
                                </div>
                                <p className="text-indigo-100 text-sm font-bold uppercase tracking-wider mb-2">Monthly Salary (Fixed)</p>
                                <h2 className="text-4xl font-black mb-6">{formatCurrency(summary?.monthlySalary || 0)}</h2>
                                <div className="inline-flex items-center gap-2 bg-indigo-500/30 px-4 py-2 rounded-xl border border-indigo-400/30 text-sm font-medium">
                                    <CheckCircle className="h-4 w-4" />
                                    Payroll Managed by Institution
                                </div>
                            </div>
                        ) : (
                            <div className="bg-indigo-600 rounded-3xl p-8 text-white shadow-xl shadow-indigo-200 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform">
                                    <Wallet className="h-32 w-32" />
                                </div>
                                <p className="text-indigo-100 text-sm font-bold uppercase tracking-wider mb-2">Available for Payout</p>
                                <h2 className="text-4xl font-black mb-6">{formatCurrency(summary?.availableToWithdraw || 0)}</h2>
                                <button 
                                    onClick={() => setWithdrawModal(true)}
                                    disabled={summary?.availableToWithdraw <= 0}
                                    className="bg-white text-indigo-600 px-6 py-3 rounded-2xl font-black text-sm hover:bg-indigo-50 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                >
                                    <Send className="h-4 w-4" />
                                    REQUEST PAYOUT
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4">
                            {summary?.compensationMethod !== 'salary' && (
                                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex items-center gap-4">
                                    <div className="p-4 bg-amber-50 rounded-2xl">
                                        <Clock className="h-6 w-6 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Locked Funds (Pending Completion)</p>
                                        <p className="text-xl font-bold text-gray-900">{formatCurrency(summary?.lockedFunds || 0)}</p>
                                    </div>
                                </div>
                            )}
                            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex items-center gap-4">
                                <div className="p-4 bg-green-50 rounded-2xl">
                                    <TrendingUp className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">{summary?.compensationMethod === 'salary' ? 'Earnings Type' : 'Lifetime Earnings'}</p>
                                    <p className="text-xl font-bold text-gray-900">{summary?.compensationMethod === 'salary' ? 'Fixed Salary' : formatCurrency(summary?.totalEarned || 0)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full md:w-80 space-y-4">
                    {summary?.compensationMethod === 'salary' ? (
                        <div className="bg-emerald-50 rounded-3xl p-6 border border-emerald-100">
                            <div className="flex items-center gap-3 mb-4">
                                <CreditCard className="h-5 w-5 text-emerald-600" />
                                <h3 className="font-bold text-emerald-900">Payroll Status</h3>
                            </div>
                            <p className="text-sm text-emerald-800 leading-relaxed">
                                You are currently on a fixed monthly salary. Your compensation is processed through the institution's payroll system. Commission breakdowns are not applicable to your account.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-blue-50 rounded-3xl p-6 border border-blue-100">
                            <div className="flex items-center gap-3 mb-4">
                                <Info className="h-5 w-5 text-blue-600" />
                                <h3 className="font-bold text-blue-900">Earning Rules</h3>
                            </div>
                            <p className="text-sm text-blue-800 leading-relaxed">
                                Earnings are <strong>Locked</strong> when a student enrolls. You must mark the unit as completed for the student in your <strong>Manage Units</strong> section to release these funds for withdrawal.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-2xl flex items-center gap-3 animate-slideDown ${
                    message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                }`}>
                    {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                    <p className="text-sm font-bold">{message.text}</p>
                    <button onClick={() => setMessage(null)} className="ml-auto text-current opacity-50">&times;</button>
                </div>
            )}

            {/* Transactions Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                            <History className="h-5 w-5 text-indigo-600" />
                            Recent Income
                        </h3>
                    </div>

                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50/50">
                                    <tr className="text-left">
                                        <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Source</th>
                                        <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Gross</th>
                                        <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Your Earning</th>
                                        <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {summary?.transactions?.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="py-12 text-center text-gray-500 italic">No earnings recorded yet.</td>
                                        </tr>
                                    ) : (
                                        summary?.transactions?.map((t) => (
                                            <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="font-bold text-gray-900">{t.unit?.name}</p>
                                                        <p className="text-xs text-gray-500">Student: {t.student?.name}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(t.grossAmount)}</td>
                                                <td className="px-6 py-4 font-black text-gray-900">
                                                    <div className="flex items-center gap-1 text-green-600">
                                                        <ArrowUpRight className="h-3 w-3" />
                                                        {formatCurrency(t.earnedAmount)}
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 uppercase font-bold">{t.commissionPercentage}% share</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                        t.withdrawalStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                                                        t.withdrawalStatus === 'requested' ? 'bg-blue-100 text-blue-700' :
                                                        'bg-green-100 text-green-700'
                                                    }`}>
                                                        {t.withdrawalStatus}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <h3 className="text-xl font-black text-gray-900">Withdrawals</h3>
                    <div className="space-y-4">
                        {summary?.withdrawals?.length === 0 ? (
                            <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-8 text-center text-gray-400 italic">
                                No payout history.
                            </div>
                        ) : (
                            summary?.withdrawals?.map((w) => (
                                <div key={w.id} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition-all group">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-xl ${
                                            w.status === 'approved' ? 'bg-green-50 text-green-600' : 
                                            w.status === 'rejected' ? 'bg-red-50 text-red-600' : 
                                            'bg-amber-50 text-amber-600'
                                        }`}>
                                            <ArrowDownLeft className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900">{formatCurrency(w.amount)}</p>
                                            <p className="text-xs text-gray-500">{new Date(w.createdAt).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase ${
                                        w.status === 'approved' ? 'bg-green-100 text-green-700' : 
                                        w.status === 'rejected' ? 'bg-red-100 text-red-700' : 
                                        'bg-amber-100 text-amber-700'
                                    }`}>
                                        {w.status}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Withdraw Modal */}
            {withdrawModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[40px] w-full max-w-lg overflow-hidden animate-scaleIn shadow-2xl">
                        <div className="p-8 space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-black text-gray-900">Request Payout</h2>
                                <button onClick={() => setWithdrawModal(false)} className="text-gray-400 hover:text-gray-600">&times;</button>
                            </div>

                            <form onSubmit={handleWithdrawRequest} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Amount to Withdraw (KES)</label>
                                    <input 
                                        type="number"
                                        value={withdrawData.amount}
                                        onChange={(e) => setWithdrawData({...withdrawData, amount: e.target.value})}
                                        className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-xl"
                                        max={summary?.totalPending}
                                        required
                                    />
                                    <p className="mt-2 text-xs text-gray-500">Max available: {formatCurrency(summary?.totalPending)}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Payment Method</label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button 
                                            type="button"
                                            onClick={() => setWithdrawData({...withdrawData, paymentMethod: 'mpesa'})}
                                            className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                                                withdrawData.paymentMethod === 'mpesa' ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-gray-100 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <CreditCard className="h-5 w-5" />
                                            <span className="font-bold">M-Pesa</span>
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setWithdrawData({...withdrawData, paymentMethod: 'bank'})}
                                            className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                                                withdrawData.paymentMethod === 'bank' ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-gray-100 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Building className="h-5 w-5" />
                                            <span className="font-bold">Bank Transfer</span>
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Payout Details</label>
                                    <input 
                                        type="text"
                                        value={withdrawData.paymentDetails}
                                        onChange={(e) => setWithdrawData({...withdrawData, paymentDetails: e.target.value})}
                                        className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                        placeholder={withdrawData.paymentMethod === 'mpesa' ? "Enter Phone Number" : "Enter Bank Account Details"}
                                        required
                                    />
                                </div>

                                <button 
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-200"
                                >
                                    {submitting ? <RefreshCw className="h-6 w-6 animate-spin" /> : 'SUBMIT REQUEST'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Earnings;
